/**
 * 🗺️ FIRESTORE PATHS (Single Source of Truth)
 * Centralizes all collection and document paths to ensure consistency
 * between Client SDK and Admin SDK.
 * 
 * V2 PIVOT: Everything is owner-centric. Root-level resources are deprecated.
 */
export const FirestorePaths = {
  // --- 1. GLOBAL / ROOT COLLECTIONS ---
  tasaDoc: 'tasa/tasa',
  globalPaymentMethods: 'global/payment-methods',
  landingMetrics: 'global/metrics',
  legalVersions: 'global/legal/versions',
  legalAcceptances: 'legal_acceptances',
  globalAdmin: 'global/admin',
  globalPixels: 'global_pixels',
  globalPixelDoc: (pixelId: string) => `global_pixels/${pixelId}`,
  
  // Slugs (The only root-level identifier for public lookups)
  slugs: 'slugs',
  slugDoc: (slug: string) => `slugs/${slug}`,
  
  // Public Mirror (For SEO and cached reads)
  public: 'public',
  publicDoc: (ownerId: string) => `public/${ownerId}`,
  
  // --- 2. OWNER-CENTRIC COLLECTIONS (The core of CRM) ---
  owners: 'owners',
  ownerDoc: (ownerId: string) => `owners/${ownerId}`,
  
  // Unified Prospects (Leads + Clients)
  clients: (ownerId: string) => `owners/${ownerId}/clients`,
  clientDoc: (ownerId: string, clientId: string) => `owners/${ownerId}/clients/${clientId}`,
  
  // Financial Orchestration
  sales: (ownerId: string) => `owners/${ownerId}/sales`,
  saleDoc: (ownerId: string, saleId: string) => `owners/${ownerId}/sales/${saleId}`,

  // NOTE: Appointments are deprecated in the CRM pivot, kept for legacy UI read-only if needed
  appointments: (ownerId: string) => `owners/${ownerId}/appointments`,
  appointmentDoc: (ownerId: string, id: string) => `owners/${ownerId}/appointments/${id}`,
  
  // Product Catalog (Formerly Services)
  products: (ownerId: string) => `owners/${ownerId}/products`,
  productDoc: (ownerId: string, productId: string) => `owners/${ownerId}/products/${productId}`,
  
  // Operations & Ledger
  transactions: (ownerId: string) => `owners/${ownerId}/transactions`,
  transactionDoc: (ownerId: string, txId: string) => `owners/${ownerId}/transactions/${txId}`,
  
  balances: (ownerId: string) => `owners/${ownerId}/balances`,
  balanceDoc: (ownerId: string, workerId: string) => `owners/${ownerId}/balances/${workerId}`,
  
  settlements: (ownerId: string) => `owners/${ownerId}/settlements`,
  settlementDoc: (ownerId: string, settlementId: string) => `owners/${ownerId}/settlements/${settlementId}`,
  
  expenses: (ownerId: string) => `owners/${ownerId}/expenses`,
  expenseDoc: (ownerId: string, expenseId: string) => `owners/${ownerId}/expenses/${expenseId}`,
  
  // --- 3. ADMIN & SETTINGS ---
  marketingConfig: (ownerId: string) => `owners/${ownerId}/admin/marketing`,
  adminSettings: (ownerId: string) => `owners/${ownerId}/admin/settings`,
  adminPrivate: (ownerId: string) => `owners/${ownerId}/admin/private`,
  adminBilling: (ownerId: string) => `owners/${ownerId}/admin/billing/records`,
  
  // --- 4. MARKETING & AI ---
  marketingThreads: (ownerId: string) => `owners/${ownerId}/marketing_threads`,
  marketingThreadDoc: (ownerId: string, phone: string) => `owners/${ownerId}/marketing_threads/${phone}`,
  marketingThreadMessages: (ownerId: string, phone: string) => `owners/${ownerId}/marketing_threads/${phone}/messages`,
  
  marketingCampaigns: (ownerId: string) => `owners/${ownerId}/marketing_campaigns`,
  marketingCampaignDoc: (ownerId: string, campaignId: string) => `owners/${ownerId}/marketing_campaigns/${campaignId}`,
  marketingAdSets: (ownerId: string) => `owners/${ownerId}/marketing_adsets`,
  marketingAdSetDoc: (ownerId: string, adsetId: string) => `owners/${ownerId}/marketing_adsets/${adsetId}`,
  marketingAds: (ownerId: string) => `owners/${ownerId}/marketing_ads`,
  marketingAdDoc: (ownerId: string, adId: string) => `owners/${ownerId}/marketing_ads/${adId}`,
  
  // --- 5. ACADEMY ---
  courses: (ownerId: string) => `owners/${ownerId}/courses`,
  courseDoc: (ownerId: string, courseId: string) => `owners/${ownerId}/courses/${courseId}`,
  enrollments: (ownerId: string) => `owners/${ownerId}/enrollments`,
  enrollmentDoc: (ownerId: string, enrollmentId: string) => `owners/${ownerId}/enrollments/${enrollmentId}`,

  // --- 6. LEGACY (For migration API only) ---
  legacyMarketingLeads: (ownerId: string) => `owners/${ownerId}/marketing_leads`,
  legacyServices: (ownerId: string) => `owners/${ownerId}/services`, // If services were under owners
  legacyGlobalServices: 'services', // Root level services
};
